'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// A camera check: getUserMedia video preview plus the facts people came for
// (resolution, frame rate, device name). The stream stays in the page; nothing
// is recorded or transmitted.

export default function BrowserWebcamTest() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [state, setState] = useState<'idle' | 'live' | 'denied' | 'error'>('idle');
  const [mirrored, setMirrored] = useState(true);
  const [info, setInfo] = useState<{
    label: string;
    width: number;
    height: number;
    fps: number;
  } | null>(null);

  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stop() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setState('idle');
    setInfo(null);
  }

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings() ?? {};
      setInfo({
        label: track?.label || 'Camera',
        width: settings.width ?? 0,
        height: settings.height ?? 0,
        fps: Math.round(settings.frameRate ?? 0),
      });
      setState('live');
      void consumeToolUsage('webcam-test', '').catch(() => undefined);
    } catch (error) {
      setState(
        error instanceof DOMException && error.name === 'NotAllowedError' ? 'denied' : 'error',
      );
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Test your webcam</h2>
          <p>
            Press Start and allow camera access: you see exactly what the camera sees, plus its real
            resolution and frame rate. Nothing is recorded and the stream never leaves your device.
          </p>
        </div>
        <span className="tool-live-badge">Camera</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <video
            aria-label="Webcam preview"
            className="tool-image-preview-canvas"
            muted
            playsInline
            ref={videoRef}
            style={{
              background: '#10141c',
              borderRadius: 12,
              minHeight: 240,
              transform: mirrored ? 'scaleX(-1)' : 'none',
              width: '100%',
            }}
          />
          {state === 'denied' ? (
            <p className="support-feedback support-feedback-error">
              Camera access was blocked. Allow it from the address-bar permission prompt and press
              Start again. The preview still never leaves this page.
            </p>
          ) : null}
          {state === 'error' ? (
            <p className="support-feedback support-feedback-error">
              No camera was found, or another app is holding it. Close video-call apps and try
              again.
            </p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          {info ? (
            <div className="tool-live-stats">
              <div>
                <strong>
                  {info.width} × {info.height}
                </strong>
                <span>actual resolution at {info.fps} fps</span>
              </div>
              <div>
                <strong style={{ overflowWrap: 'anywhere' }}>{info.label}</strong>
                <span>active camera</span>
              </div>
            </div>
          ) : null}

          <label className="tool-live-checkbox">
            <input
              checked={mirrored}
              onChange={(event) => setMirrored(event.target.checked)}
              type="checkbox"
            />
            <span>Mirror the preview, the way call apps show you</span>
          </label>

          <p className="tool-live-panel-note">
            The preview is mirrored by default because that is how you see yourself in call apps;
            other people see the unmirrored image. If the resolution reads lower than the camera’s
            spec, the browser negotiated down, usually over USB bandwidth or lighting.
          </p>
        </aside>
      </div>

      <div className="hero-actions tool-live-actions">
        {state === 'live' ? (
          <button className="button button-ghost" onClick={stop} type="button">
            Stop the camera
          </button>
        ) : (
          <button className="button" onClick={() => void start()} type="button">
            Start the camera test
          </button>
        )}
      </div>
    </article>
  );
}
